import type { SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import IDCardSkeletonLoader from "../id-card/id-card-skeleton-loader";
import { NoDataAvailable } from "@/pages/components/empty";

/*
 * The worker has to resolve to a same-origin asset: workers fall back through
 * worker-src -> child-src -> script-src, and the deployed script-src is 'self'
 * plus a few Google hosts, so any bundler pattern that spawns the worker from a
 * blob: URL is blocked in production. import.meta.url makes Vite emit a local
 * hashed file instead. See CSRTB-1034.
 */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

type PdfViewerCommonProps = {
  pageNumber?: number; // if not passed as props, renders all pages
  maxWidth?: number;
};

/**
 * Accepts a base64 payload (what the API returns), raw bytes (what a picked
 * File or a fetched Blob gives up via arrayBuffer()), or a URL the document can
 * be fetched from. Exactly one of the three is required.
 */
type Base64PdfViewerProps = PdfViewerCommonProps &
  (
    | { base64Encoded: string; data?: never; url?: never }
    | { data: Uint8Array; base64Encoded?: never; url?: never }
    | { url: string; base64Encoded?: never; data?: never }
  );

function base64ToBytes(base64Encoded: string): Uint8Array {
  return Uint8Array.from(atob(base64Encoded), (c) => c.charCodeAt(0));
}

export default function Base64PdfViewer({
  base64Encoded,
  data,
  url,
  pageNumber,
  maxWidth = 800,
}: Base64PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const [numPages, setNumPages] = useState<number | null>(null);

  const bytes = useMemo(() => {
    if (data) return data;
    return base64Encoded ? base64ToBytes(base64Encoded) : null;
  }, [data, base64Encoded]);

  /*
   * Two reasons this is memoised rather than an inline literal.
   *
   * react-pdf reloads the document whenever `file` changes identity, so a fresh
   * object each render re-parses the PDF on every unrelated state update.
   *
   * And pdf.js transfers the buffer to its worker, which detaches it -- handing
   * the same array to a second load throws on a detached ArrayBuffer. Passing a
   * copy keeps the caller's array reusable, which matters for callers that hand
   * back the same bytes when a document is revisited.
   */
  const file = useMemo(() => {
    if (bytes) return { data: bytes.slice() };
    // react-pdf fetches a URL itself, so it is handed straight through.
    return url ?? null;
  }, [bytes, url]);

  const updateWidth = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      setWidth(Math.min(containerWidth, maxWidth));
    }
  }, [maxWidth]);

  useEffect(() => {
    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateWidth);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [updateWidth]);

  const onLoadSuccess = (pdf: { numPages: SetStateAction<number | null> }) => {
    setNumPages(pdf.numPages);
  };

  return (
    <div ref={containerRef} className="w-full">
      {width > 0 && file && (
        <Document
          file={file}
          onLoadSuccess={onLoadSuccess}
          externalLinkTarget="_blank"
          loading={<IDCardSkeletonLoader />}
          error={<NoDataAvailable />}
        >
          {pageNumber ? (
            <div className="flex justify-center">
              <Page pageNumber={pageNumber} width={width} />
            </div>
          ) : (
            Array.from({ length: numPages ?? 0 }, (_, index) => (
              <div key={index} className="mb-4 flex justify-center">
                <Page pageNumber={index + 1} width={width} />
              </div>
            ))
          )}
        </Document>
      )}
    </div>
  );
}
